/**
 * SNES-style Synthesizer
 *
 * Main thread controller for the SNES AudioWorklet processor.
 * Provides a clean API for loading samples, triggering notes, and controlling parameters.
 */

import workletUrl from './SnesWorkletProcessor.ts?worker&url';

export interface SampleOptions {
  sampleRate?: number;
  loopStart?: number;
  loopEnd?: number;
  loopEnabled?: boolean;
}

// Gain modes for S-DSP (when ADSR is disabled)
export enum GainMode {
  Direct = 0, // E = gainValue * 16 (direct set, no rate)
  LinearDecrease = 1, // E -= 32 at rate
  ExpDecrease = 2, // E -= ((E-1)>>8)+1 at rate
  LinearIncrease = 3, // E += 32 at rate
  BentIncrease = 4, // E += (E<0x600)?32:8 at rate
}

export interface EnvelopeOptions {
  // Mode selection
  adsrEnabled?: boolean; // true = ADSR mode (default), false = GAIN mode

  // ADSR parameters (when adsrEnabled = true)
  attack?: number; // 0-15 (Attack rate: R=attack*2+1, or R=31 if attack=15)
  decay?: number; // 0-7 (Decay rate: R=decay*2+16)
  sustainLevel?: number; // 0-7 (Sustain level threshold, 7 = full volume)
  sustainDecay?: number; // 0-31 (Sustain decay speed - 0=hold forever, 31=fast decay)

  // GAIN parameters (when adsrEnabled = false)
  gainMode?: GainMode; // Which gain mode to use
  gainRate?: number; // 0-31 (rate for non-direct modes, 31=fastest)
  gainValue?: number; // 0-127 (for direct mode: E = gainValue * 16)
}

export interface EchoOptions {
  enabled?: boolean;
  delay?: number; // milliseconds (0-240)
  feedback?: number; // -1 to 1
  volumeL?: number; // 0-1
  volumeR?: number; // 0-1
  fir?: number[]; // 8-tap FIR coefficients (-1 to 1 each)
}

// Preset FIR filters that sound good
export const FIR_PRESETS = {
  // Flat response
  flat: [1, 0, 0, 0, 0, 0, 0, 0],
  // Low-pass (muffled)
  lowpass: [0.5, 0.25, 0.125, 0.0625, 0.03125, 0, 0, 0],
  // Echo with decay
  echoDecay: [0.7, 0.3, 0.15, 0.08, 0.04, 0.02, 0.01, 0.005],
  // Comb filter (metallic)
  comb: [1, 0, 0, 0, 0.5, 0, 0, 0],
  // High-pass-ish
  highpass: [1, -0.5, 0.25, -0.125, 0.0625, 0, 0, 0],
  // Classic SNES reverb sound
  snesHall: [0.8, 0.2, 0.1, 0.05, 0.025, 0.0125, 0.00625, 0.003125],
};

export class SnesSynth {
  private audioContext: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  /**
   * Initialize the audio system. Must be called from a user gesture.
   */
  async init(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._init();
    return this.initPromise;
  }

  private async _init(): Promise<void> {
    this.audioContext = new AudioContext({ sampleRate: 48000 });

    // Load the worklet module
    await this.audioContext.audioWorklet.addModule(workletUrl);

    // Create the worklet node
    this.workletNode = new AudioWorkletNode(this.audioContext, 'snes-worklet-processor', {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
    });

    // Connect to destination
    this.workletNode.connect(this.audioContext.destination);

    // Start the message port to receive messages from worklet
    this.workletNode.port.start();

    this.initialized = true;
  }

  /**
   * Resume audio context if suspended (needed after init in some browsers)
   */
  async resume(): Promise<void> {
    if (this.audioContext?.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  /**
   * Load a sample into a voice
   */
  loadSample(voice: number, sampleData: Float32Array, options: SampleOptions = {}): void {
    this.send({
      type: 'loadSample',
      voice,
      sampleData,
      sampleRate: options.sampleRate ?? 32000,
      loopStart: options.loopStart ?? 0,
      loopEnd: options.loopEnd ?? sampleData.length,
      loopEnabled: options.loopEnabled ?? false,
    });
  }

  /**
   * Load a sample from an AudioBuffer
   */
  loadSampleFromBuffer(voice: number, buffer: AudioBuffer, options: SampleOptions = {}): void {
    // Convert to mono Float32Array
    const samples = new Float32Array(buffer.length);
    const channelData = buffer.getChannelData(0);
    samples.set(channelData);

    // If stereo, mix to mono
    if (buffer.numberOfChannels > 1) {
      const rightChannel = buffer.getChannelData(1);
      for (let i = 0; i < samples.length; i++) {
        samples[i] = (samples[i] + rightChannel[i]) * 0.5;
      }
    }

    this.loadSample(voice, samples, {
      sampleRate: buffer.sampleRate,
      ...options,
    });
  }

  /**
   * Load a sample from a URL
   */
  async loadSampleFromUrl(voice: number, url: string, options: SampleOptions = {}): Promise<void> {
    if (!this.audioContext) throw new Error('SnesSynth not initialized');

    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

    this.loadSampleFromBuffer(voice, audioBuffer, options);
  }

  /**
   * Generate a simple waveform sample (single cycle for seamless looping)
   *
   * Returns a single cycle at a base frequency. Use pitch parameter in noteOn
   * to play at different frequencies.
   *
   * @param type Waveform type
   * @param samplesPerCycle Samples per cycle (higher = better quality, default 256)
   * @param sampleRate Sample rate for pitch calculations (default 32000)
   * @returns Object with samples and the base frequency
   */
  generateWaveform(
    type: 'sine' | 'square' | 'saw' | 'triangle' | 'noise',
    samplesPerCycle: number = 256,
    sampleRate: number = 32000
  ): { samples: Float32Array; baseFrequency: number } {
    // For noise, generate a longer buffer since it doesn't loop
    const length = type === 'noise' ? sampleRate : samplesPerCycle;
    const samples = new Float32Array(length);

    for (let i = 0; i < length; i++) {
      const phase = i / samplesPerCycle; // 0 to 1 over one cycle

      switch (type) {
        case 'sine':
          samples[i] = Math.sin(2 * Math.PI * phase);
          break;
        case 'square':
          samples[i] = phase % 1 < 0.5 ? 0.8 : -0.8; // Slightly reduced to avoid harsh clipping
          break;
        case 'saw':
          samples[i] = 2 * (phase % 1) - 1;
          break;
        case 'triangle':
          {
            const p = phase % 1;
            samples[i] = p < 0.5 ? 4 * p - 1 : 3 - 4 * p;
          }
          break;
        case 'noise':
          samples[i] = Math.random() * 2 - 1;
          break;
      }
    }

    // Base frequency = how many cycles per second at this sample rate
    const baseFrequency = sampleRate / samplesPerCycle;

    return { samples, baseFrequency };
  }

  /**
   * Helper to load a generated waveform into a voice
   * Returns the base frequency (pitch=1.0 plays this frequency)
   */
  loadWaveform(
    voice: number,
    type: 'sine' | 'square' | 'saw' | 'triangle' | 'noise',
    sampleRate: number = 32000
  ): number {
    // Use 256 samples per cycle = 125Hz base frequency at 32kHz
    // This gives audible output at pitch=1.0 and reasonable quality
    const { samples, baseFrequency } = this.generateWaveform(type, 256, sampleRate);
    this.loadSample(voice, samples, {
      sampleRate,
      loopEnabled: type !== 'noise',
      loopEnd: samples.length,
    });
    return baseFrequency;
  }

  /**
   * Trigger a note on
   */
  noteOn(voice: number, pitch: number = 1.0): void {
    this.send({ type: 'noteOn', voice, pitch });
  }

  /**
   * Trigger a note off (starts release phase)
   */
  noteOff(voice: number): void {
    this.send({ type: 'noteOff', voice });
  }

  /**
   * Play a note with automatic note-off after duration
   */
  playNote(voice: number, pitch: number = 1.0, duration: number = 0.5): void {
    this.noteOn(voice, pitch);
    setTimeout(() => this.noteOff(voice), duration * 1000);
  }

  /**
   * Set pitch for a voice (1.0 = original pitch, 2.0 = octave up, 0.5 = octave down)
   */
  setPitch(voice: number, pitch: number): void {
    this.send({ type: 'setPitch', voice, pitch });
  }

  /**
   * Set pitch by semitones relative to base pitch
   */
  setPitchBySemitones(voice: number, semitones: number): void {
    const pitch = Math.pow(2, semitones / 12);
    this.setPitch(voice, pitch);
  }

  /**
   * Set envelope for a voice (ADSR or GAIN mode)
   *
   * ADSR mode (adsrEnabled = true, default):
   * - attack: 0-15 (rate, 15 = instant)
   * - decay: 0-7 (rate)
   * - sustainLevel: 0-7 (threshold)
   * - sustainDecay: 0-31 (rate, 0 = hold forever)
   *
   * GAIN mode (adsrEnabled = false):
   * - gainMode: Direct, LinearDecrease, ExpDecrease, LinearIncrease, BentIncrease
   * - gainRate: 0-31 (rate for non-direct modes)
   * - gainValue: 0-127 (for Direct mode)
   */
  setEnvelope(voice: number, options: EnvelopeOptions): void {
    this.send({
      type: 'setEnvelope',
      voice,
      // Mode selection
      adsrEnabled: options.adsrEnabled,
      // ADSR parameters
      attack: options.attack,
      decay: options.decay,
      sustainLevel: options.sustainLevel,
      sustainDecay: options.sustainDecay,
      // GAIN parameters
      gainMode: options.gainMode,
      gainRate: options.gainRate,
      gainValue: options.gainValue,
    });
  }

  /**
   * Set volume for a voice (stereo)
   */
  setVolume(voice: number, volumeL: number, volumeR?: number): void {
    this.send({
      type: 'setVolume',
      voice,
      volumeL,
      volumeR: volumeR ?? volumeL,
    });
  }

  /**
   * Set volume by pan position (-1 = full left, 0 = center, 1 = full right)
   */
  setVolumeWithPan(voice: number, volume: number, pan: number): void {
    // Equal power panning
    const angle = ((pan + 1) / 2) * (Math.PI / 2);
    const volumeL = volume * Math.cos(angle);
    const volumeR = volume * Math.sin(angle);
    this.setVolume(voice, volumeL, volumeR);
  }

  /**
   * Configure echo effect
   */
  setEcho(options: EchoOptions): void {
    this.send({
      type: 'setEcho',
      echoEnabled: options.enabled,
      echoDelay: options.delay,
      echoFeedback: options.feedback,
      echoVolumeL: options.volumeL,
      echoVolumeR: options.volumeR,
      echoFir: options.fir,
    });
  }

  /**
   * Set main output volume
   */
  setMainVolume(volumeL: number, volumeR?: number): void {
    this.send({
      type: 'setMainVolume',
      mainVolumeL: volumeL,
      mainVolumeR: volumeR ?? volumeL,
    });
  }

  /**
   * Convert MIDI note number to pitch multiplier (middle C = 60 = pitch 1.0 at 440Hz base)
   */
  midiNoteToPitch(noteNumber: number, baseNote: number = 60): number {
    return Math.pow(2, (noteNumber - baseNote) / 12);
  }

  /**
   * Get the audio context (for advanced usage)
   */
  getAudioContext(): AudioContext | null {
    return this.audioContext;
  }

  /**
   * Check if initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Start capturing audio output for debugging
   */
  startCapture(): void {
    this.send({ type: 'startCapture' });
  }

  /**
   * Stop capturing and get the captured data via callback
   */
  stopCapture(callback: (samples: number[], sampleRate: number) => void): void {
    if (!this.workletNode) return;

    const handler = (event: MessageEvent) => {
      if (event.data.type === 'captureData') {
        callback(event.data.samples, event.data.sampleRate);
        this.workletNode?.port.removeEventListener('message', handler);
      }
    };
    this.workletNode.port.addEventListener('message', handler);
    this.send({ type: 'stopCapture' });
  }

  /**
   * Get the worklet node for advanced usage (e.g., setting up message handlers)
   */
  getWorkletNode(): AudioWorkletNode | null {
    return this.workletNode;
  }

  // ===========================================================================
  // Scheduled Commands (for sample-accurate timing from composition engine)
  // ===========================================================================

  /**
   * Synchronize the worklet's time tracking with the AudioContext.
   * Call this when starting playback to establish the time reference.
   */
  syncTime(): void {
    if (!this.audioContext) return;
    this.send({ type: 'syncTime', time: this.audioContext.currentTime });
  }

  /**
   * Schedule a note-on at a specific AudioContext time.
   * The note will be triggered with sample-accurate timing in the worklet.
   */
  scheduleNoteOn(voice: number, pitch: number, time: number): void {
    this.send({ type: 'scheduledNoteOn', voice, pitch, time });
  }

  /**
   * Schedule a note-off at a specific AudioContext time.
   * The note-off will be triggered with sample-accurate timing in the worklet.
   */
  scheduleNoteOff(voice: number, time: number): void {
    this.send({ type: 'scheduledNoteOff', voice, time });
  }

  /**
   * Clear all pending scheduled commands.
   * Call this on stop/seek to prevent stale commands from executing.
   */
  clearScheduledCommands(): void {
    this.send({ type: 'clearScheduledCommands' });
  }

  // ===========================================================================
  // Sample Library (for dynamic voice assignment)
  // ===========================================================================

  /**
   * Load a sample into the worklet's sample library.
   * Samples are stored by instrument ID for later assignment to voices.
   * Call this once per instrument at project load time.
   */
  loadSampleToLibrary(
    instrumentId: string,
    sampleData: Float32Array,
    options: SampleOptions = {}
  ): void {
    this.send({
      type: 'loadSampleToLibrary',
      instrumentId,
      sampleData,
      sampleRate: options.sampleRate ?? 32000,
      loopStart: options.loopStart ?? 0,
      loopEnd: options.loopEnd ?? sampleData.length,
      loopEnabled: options.loopEnabled ?? false,
    });
  }

  /**
   * Schedule a voice to use a sample from the library.
   * This assigns the sample data from instrumentId to the specified voice
   * at a specific AudioContext time.
   */
  scheduleSetVoiceSource(voice: number, instrumentId: string, time: number): void {
    this.send({ type: 'scheduledSetVoiceSource', voice, instrumentId, time });
  }

  /**
   * Schedule envelope configuration at a specific AudioContext time.
   * Use this to set up envelope parameters just before a noteOn.
   */
  scheduleSetEnvelope(voice: number, options: EnvelopeOptions, time: number): void {
    this.send({
      type: 'scheduledSetEnvelope',
      voice,
      time,
      adsrEnabled: options.adsrEnabled,
      attack: options.attack,
      decay: options.decay,
      sustainLevel: options.sustainLevel,
      sustainDecay: options.sustainDecay,
      gainMode: options.gainMode,
      gainRate: options.gainRate,
      gainValue: options.gainValue,
    });
  }

  private send(data: Record<string, unknown>): void {
    if (!this.workletNode) {
      console.warn('SnesSynth not initialized');
      return;
    }
    this.workletNode.port.postMessage(data);
  }
}

// Singleton instance for convenience
export const snesSynth = new SnesSynth();
